import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import * as React from "react";

const ReactJson = React.lazy(() => import("@microlink/react-json-view"));
const HighlightedCodeBody = React.lazy(() => import("./HighlightedCodeBody"));

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import {
  type ChatToolResultContent,
  type ChatToolUseContent,
} from "@/shared/domain/chat";
import { useToolDetails } from "@/shared/domain/chat/hooks";

import { CodeBlock } from "./CodeBlock";
import styles from "./ToolDetailPanel.module.css";

const MS_TO_SECONDS_THRESHOLD = 100;

function formatRuntime(runtimeMs: number): string {
  if (runtimeMs < MS_TO_SECONDS_THRESHOLD) {
    return `${runtimeMs}ms`;
  }

  return `${(runtimeMs / 1000).toFixed(2)}s`;
}

/**
 * If raw_request has an `input` key (Anthropic tool_use format),
 * return just the input. Otherwise return as-is.
 */
function extractInput(
  rawRequest: Record<string, unknown> | undefined,
): unknown {
  if (!rawRequest) {
    return null;
  }

  if ("input" in rawRequest) {
    return rawRequest.input;
  }

  return rawRequest;
}

const SQL_FIELD_NAMES = ["sql_query", "sql"] as const;

function extractSqlField(
  input: unknown,
): { code: string; fieldName: string } | null {
  if (!isPlainObject(input)) {
    return null;
  }
  for (const key of SQL_FIELD_NAMES) {
    const v = input[key];
    if (typeof v === "string" && v.trim().length > 0) {
      return { code: v, fieldName: key };
    }
  }
  return null;
}

function omitKey(input: unknown, key: string): unknown {
  if (!isPlainObject(input)) {
    return input;
  }
  const copy = { ...input };
  delete copy[key];
  return copy;
}

/**
 * Unwrap the Anthropic toolResult > content > text pattern if present.
 * Attempts to parse JSON-encoded text for pretty display.
 * Falls back to raw_response as-is.
 */
function extractOutput(
  rawResponse: Record<string, unknown> | undefined,
): unknown {
  if (!rawResponse) {
    return null;
  }

  const toolResult = rawResponse.toolResult;
  if (
    typeof toolResult === "object" &&
    toolResult !== null &&
    "content" in toolResult
  ) {
    const result = toolResult as Record<string, unknown>;
    const content = result.content;

    if (Array.isArray(content)) {
      const textParts: unknown[] = [];

      for (const block of content) {
        if (typeof block === "object" && block !== null && "text" in block) {
          const textValue = (block as Record<string, unknown>).text;

          if (typeof textValue === "string") {
            try {
              textParts.push(JSON.parse(textValue));
            } catch {
              textParts.push(textValue);
            }
          } else {
            textParts.push(textValue);
          }
        } else {
          textParts.push(block);
        }
      }

      if (textParts.length === 1) {
        return textParts[0];
      }

      if (textParts.length > 1) {
        return textParts;
      }
    }
  }

  return rawResponse;
}

function useIsDarkMode(): boolean {
  const [isDark, setIsDark] = React.useState(
    () => document.documentElement.getAttribute("data-color-scheme") === "dark",
  );

  React.useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(
        document.documentElement.getAttribute("data-color-scheme") === "dark",
      );
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-color-scheme"],
    });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toJsonViewSrc(value: unknown): object {
  if (isPlainObject(value) || Array.isArray(value)) {
    return value as object;
  }
  // ReactJson requires an object/array as src — wrap primitives
  return { value };
}

function stringifyForCopy(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  return JSON.stringify(value ?? null, null, 2) ?? "null";
}

function CopyButton({ value }: { value: unknown }): React.JSX.Element {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(stringifyForCopy(value))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // Silently fail — clipboard may not be available
      });
  };

  return (
    <button
      type="button"
      className={styles.copyButton}
      onClick={handleCopy}
      aria-label="Copy to clipboard"
      title={copied ? "Copied!" : "Copy"}
    >
      {copied ? (
        <DoneIcon className={styles.copyIcon} />
      ) : (
        <ContentCopyIcon className={styles.copyIcon} />
      )}
    </button>
  );
}

interface JsonSectionProps {
  label: string;
  value: unknown;
  isDarkMode: boolean;
}

function JsonSection({
  label,
  value,
  isDarkMode,
}: JsonSectionProps): React.JSX.Element {
  const isStructured = isPlainObject(value) || Array.isArray(value);

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <RobotoTypography component="p" className={styles.sectionTitle}>
          {label}
        </RobotoTypography>
        <CopyButton value={value} />
      </div>
      {isStructured ? (
        <div className={styles.jsonBlock}>
          <React.Suspense
            fallback={<pre>{JSON.stringify(value, null, 2)}</pre>}
          >
            <ReactJson
              src={toJsonViewSrc(value)}
              theme={isDarkMode ? "monokai" : "rjv-default"}
              name={false}
              displayDataTypes={false}
              displayObjectSize={false}
              enableClipboard={false}
              collapsed={2}
              style={{
                backgroundColor: "transparent",
                fontFamily: "inherit",
                fontSize: "inherit",
              }}
            />
          </React.Suspense>
        </div>
      ) : (
        <pre className={styles.jsonBlock}>
          {typeof value === "string"
            ? value
            : JSON.stringify(value ?? null, null, 2)}
        </pre>
      )}
    </div>
  );
}

interface ToolDetailPanelContentProps {
  toolResult: ChatToolResultContent;
  toolUse: ChatToolUseContent;
}

function ToolDetailPanelContent({
  toolResult,
  toolUse,
}: ToolDetailPanelContentProps): React.JSX.Element {
  const isDarkMode = useIsDarkMode();
  const isSuccess = toolResult.status === "success";

  const input = toolUse.input ?? extractInput(toolUse.raw_request);
  const output = extractOutput(toolResult.raw_response);

  const sqlField = extractSqlField(input);
  const inputForJson = sqlField ? omitKey(input, sqlField.fieldName) : input;
  const showInputJson =
    !sqlField ||
    (isPlainObject(inputForJson) && Object.keys(inputForJson).length > 0);

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <RobotoTypography component="p" className={styles.toolName}>
            {toolResult.tool_name}
          </RobotoTypography>
          <RobotoTypography component="p" className={styles.toolUseId}>
            {toolResult.tool_use_id}
          </RobotoTypography>
        </div>
        <div className={styles.headerRight}>
          <span
            className={
              isSuccess ? styles.statusBadgeSuccess : styles.statusBadgeError
            }
          >
            {isSuccess ? (
              <CheckCircleOutlineIcon className={styles.statusIcon} />
            ) : (
              <ErrorOutlineIcon className={styles.statusIcon} />
            )}
            {isSuccess ? "Success" : "Failed"}
          </span>
          <RobotoTypography component="span" className={styles.runtime}>
            {formatRuntime(toolResult.runtime_ms)}
          </RobotoTypography>
        </div>
      </div>

      {sqlField && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <RobotoTypography component="p" className={styles.sectionTitle}>
              {sqlField.fieldName}
            </RobotoTypography>
          </div>
          <CodeBlock
            code={sqlField.code}
            language="sql"
            highlightedChildren={
              <React.Suspense fallback={<code>{sqlField.code}</code>}>
                <HighlightedCodeBody code={sqlField.code} language="sql" />
              </React.Suspense>
            }
          />
        </div>
      )}
      {showInputJson && (
        <JsonSection
          label={sqlField ? "Other input" : "Input"}
          value={inputForJson}
          isDarkMode={isDarkMode}
        />
      )}
      <JsonSection label="Output" value={output} isDarkMode={isDarkMode} />
    </div>
  );
}

interface ToolDetailPanelProps {
  chatId: string;
  toolUseId: string;
}

export function ToolDetailPanel({
  chatId,
  toolUseId,
}: ToolDetailPanelProps): React.JSX.Element | null {
  const { data, error, isFetching, isPending, refetch } = useToolDetails(
    chatId,
    toolUseId,
  );

  React.useEffect(() => {
    void refetch();
  }, [chatId, refetch, toolUseId]);

  if ((isPending || isFetching) && data === undefined) {
    return (
      <div className={styles.panel}>
        <RobotoTypography component="p" className={styles.loadingText}>
          Loading tool details…
        </RobotoTypography>
      </div>
    );
  }

  if (error && data === undefined) {
    return (
      <div className={styles.panel}>
        <RobotoTypography component="p" className={styles.toolName}>
          Failed to load tool details
        </RobotoTypography>
        <RobotoTypography component="p" className={styles.errorText}>
          {error.message}
        </RobotoTypography>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <ToolDetailPanelContent
      toolResult={data.tool_result}
      toolUse={data.tool_use}
    />
  );
}
